/**
 * Keeps Bugsnag and GA4 in step with the app: who is signed in, and where they
 * are. Renders nothing.
 *
 * Follows the `ThemeSync` / `LanguageSync` pattern and, like them, must sit
 * inside both `AuthProvider` (it reads the user) and the data router (it reads
 * the location). Both hooks throw outside their provider, so the placement in
 * `main.tsx` is load-bearing rather than stylistic.
 *
 * Both integrations are no-ops when unconfigured, so this component is inert in
 * a local build rather than needing to be conditionally mounted.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setBugsnagUser } from '../lib/bugsnag';
import { setAnalyticsUser, trackPageView } from '../lib/analytics';

export function ObservabilitySync() {
  const { user } = useAuth();
  const location = useLocation();
  const syncedUserRef = useRef<string | null>(null);

  // Identity. Runs on login, on logout, and on a switch between accounts.
  useEffect(() => {
    const userId = user?.userId ?? null;
    if (syncedUserRef.current === userId) return;
    syncedUserRef.current = userId;

    if (userId) {
      // Bugsnag gets the email: it is first-party, the data stays in the
      // company's own project, and knowing *who* hit an error is the point.
      setBugsnagUser({ userId, email: user?.email, name: user?.name });
      // GA4 gets the opaque id only — never the email. See setAnalyticsUser.
      setAnalyticsUser(userId);
    } else {
      setBugsnagUser(undefined);
      setAnalyticsUser(undefined);
    }
  }, [user?.userId, user?.email, user?.name]);

  // Page views. ATLAS is a single-page app, so GA4's automatic pageview
  // collection fires once on load and never again — every subsequent
  // navigation has to be sent explicitly or the whole app reads as one page.
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

export default ObservabilitySync;
